import { cached } from '@glimmer/tracking';
import InputFilter from 'volta/components/filters/input-filter';
import { columnsToProps, columnsToSortOptions } from 'volta/models/collection-view';
import { ForecastProjectHeaderResourceName } from 'volta/models/forecast-project-header';
import CollectionBuilderBase from './collection-builder-base';

import type Owner from '@ember/owner';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type {
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICreateCollectionViewCmd
} from 'volta/models/types/collection-view';

export default class ForecastProjectsCollectionDefinitionBuilder extends CollectionBuilderBase {
  resourceName = ForecastProjectHeaderResourceName;

  @cached
  get definitionsAndColumns() {
    return {
      definitions: [...this.customFieldDefinitionsService.skusDefinitions],
      columns: this.buildColumns()
    };
  }

  constructor(owner: Owner) {
    super(owner);
    this.customFieldDefinitionsService.fetchSkusDefinitions();
  }

  buildColumns() {
    const { intl } = this;
    const cols: IBvTableColumn[] = [
      {
        columnName: intl.t('name'),
        sortKey: 'name',
        valuePath: 'name',
        width: 50,
        isReorderable: true,
        isResizable: false
      },
      {
        columnName: intl.t('description'),
        sortKey: 'description',
        valuePath: 'description',
        width: 100,
        isReorderable: true,
        isResizable: false
      },
      {
        columnName: intl.t('createdAt'),
        sortKey: 'createdAt',
        valuePath: 'createdAt',
        width: 100,
        isReorderable: true,
        isResizable: true
      }
    ];

    return cols;
  }

  override buildFilters(_queryParamsValues: TQueryParams) {
    const normalDefs: TFilterDefinition[] = [];

    normalDefs.push({
      queryParam: 'q',
      apiParam: 'q',
      comparators: ['lk'],
      component: InputFilter as TBaseFilterComponent,
      hidden: true,
      componentArgs: { inputType: 'text' }
    });

    return [...normalDefs];
  }

  override buildDefaultView(columns: IBvTableColumn[]): ICreateCollectionViewCmd {
    const { intl, sessionUser } = this;

    const properties = columnsToProps(columns);

    return {
      name: intl.t('collectionViewsPage.default.name', {
        resourceNamePlural: intl.t(ForecastProjectHeaderResourceName.plural)
      }),
      description: intl.t('collectionViewsPage.default.description', {
        resourceNamePlural: intl.t(ForecastProjectHeaderResourceName.plural)
      }),
      collectionType: 'LIST',
      entityType: ForecastProjectHeaderResourceName.plural,
      properties,
      createdBy: sessionUser.userId,
      sortBy: [{ property: 'createdAt', asc: false }],
      pageSize: 100
    };
  }

  override build(queryParams: TQueryParams, sorts: ICollectionSortOption[] = []) {
    const filterDefinitions = this.buildFilters(queryParams);
    const columns = this.buildColumns();
    const defaultView = this.buildDefaultView(columns);

    const view: ICollectionViewDefinition = {
      resourceName: ForecastProjectHeaderResourceName,
      sortLimit: 0,
      groupMin: 1,
      sorts: [...columnsToSortOptions(columns), ...sorts] as ICollectionSortOption[],
      properties: columnsToProps(columns),
      filters: filterDefinitions,
      definitions: columns,
      types: ['LIST'],
      defaultView
    };

    return view;
  }
}

declare module '@ember/service' {
  interface Registry {
    'collection-builder-forecast-projects': ForecastProjectsCollectionDefinitionBuilder;
  }
}
